import { signOut } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { onAdminGateBlocked } from "./api.ts";
import { CommunitySelector } from "./CommunitySelector.tsx";
import { ErrorBanner } from "./ErrorBanner.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";
import { auth } from "./firebase.ts";
import { tabHref, useCommunity } from "./community.ts";
import { MOCK_MODE } from "./mock.ts";
import { useAuth } from "./useAuth.ts";
import { useHash } from "./useHash.ts";
import { AllRestaurants } from "./views/AllRestaurants.tsx";
import { Content } from "./views/Content.tsx";
import { Listings } from "./views/Listings.tsx";
import { NoAccess } from "./views/NoAccess.tsx";
import { Reports } from "./views/Reports.tsx";
import { SafetyNotes } from "./views/SafetyNotes.tsx";
import { SignIn } from "./views/SignIn.tsx";

type Route =
  | { kind: "reports" }
  | { kind: "restaurants-pending" }
  | { kind: "restaurants-all" }
  | { kind: "listings" }
  | { kind: "safety-notes" }
  | { kind: "content"; targetKind: "listing" | "safety_note"; id: string };

function parseRoute(hash: string): Route {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const path = raw.split("?")[0] ?? "";
  const m = path.match(/^content\/(listing|safety-note)\/(.+)$/);
  if (m) {
    return {
      kind: "content",
      targetKind: m[1] === "listing" ? "listing" : "safety_note",
      id: decodeURIComponent(m[2] ?? ""),
    };
  }
  if (path === "restaurants") return { kind: "restaurants-pending" };
  if (path === "all-restaurants") return { kind: "restaurants-all" };
  if (path === "listings") return { kind: "listings" };
  if (path === "safety-notes") return { kind: "safety-notes" };
  return { kind: "reports" };
}

export function App() {
  const { user, ready } = useAuth();
  const hash = useHash();
  const community = useCommunity();
  const [adminBlockCode, setAdminBlockCode] = useState<string | null>(null);
  const [communitiesError, setCommunitiesError] = useState<string | null>(null);

  useEffect(() => onAdminGateBlocked((code) => setAdminBlockCode(code)), []);

  // Drop the block whenever the signed-in user changes / signs out, so a
  // subsequent sign-in with a real admin account gets the full UI.
  useEffect(() => {
    if (!user) setAdminBlockCode(null);
  }, [user]);

  const handleCommunitiesError = useCallback(
    (code: string | null) => setCommunitiesError(code),
    [],
  );

  if (!ready) {
    return (
      <div className="shell">
        <div className="spinner-block">Loading…</div>
      </div>
    );
  }

  // MOCK_MODE-only: `#?preview=signin` forces the SignIn card so we can
  // screenshot it from the mock harness. Dead code in prod.
  const previewSignin = MOCK_MODE && hash.includes("preview=signin");
  if (!user || previewSignin) {
    return (
      <div className="shell">
        <SignIn />
      </div>
    );
  }

  // MOCK_MODE preview hook: `#?preview=noaccess` forces the NoAccess card so
  // we can screenshot it without a real admin-gate failure. Statically false
  // in production builds, so this branch is dead code there.
  const previewBlockCode =
    MOCK_MODE && hash.includes("preview=noaccess") ? "forbidden" : null;
  const effectiveBlockCode = adminBlockCode ?? previewBlockCode;

  if (effectiveBlockCode) {
    return (
      <div className="shell">
        <NoAccess user={user} code={effectiveBlockCode} />
      </div>
    );
  }

  const route = parseRoute(hash);
  // Content view shares the "Reports" tab in the chrome.
  const activeTab: Route["kind"] =
    route.kind === "content" ? "reports" : route.kind;

  return (
    <div className="shell">
      <header className="top">
        <h1 className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <span>Expat Admin</span>
          <span className="brand-tag">moderation</span>
        </h1>
        <div className="user">
          <span className="user-email">{user.email ?? user.uid}</span>
          <ThemeToggle />
          <button
            type="button"
            className="luna-btn luna-btn-ghost luna-btn-sm"
            onClick={() => void signOut(auth)}
          >
            Sign out
          </button>
        </div>
      </header>
      <div className="community-bar">
        <CommunitySelector onError={handleCommunitiesError} />
      </div>
      {communitiesError && <ErrorBanner code={communitiesError} />}
      <nav className="tabs">
        <a
          href={tabHref("reports", community)}
          className={activeTab === "reports" ? "active" : ""}
          data-route="reports"
        >
          Reports
        </a>
        <a
          href={tabHref("restaurants", community)}
          className={activeTab === "restaurants-pending" ? "active" : ""}
          data-route="restaurants"
        >
          Pending restaurants
        </a>
        <a
          href={tabHref("all-restaurants", community)}
          className={activeTab === "restaurants-all" ? "active" : ""}
          data-route="all-restaurants"
        >
          All restaurants
        </a>
        <a
          href={tabHref("listings", community)}
          className={activeTab === "listings" ? "active" : ""}
          data-route="listings"
        >
          Listings
        </a>
        <a
          href={tabHref("safety-notes", community)}
          className={activeTab === "safety-notes" ? "active" : ""}
          data-route="safety-notes"
        >
          Safety notes
        </a>
      </nav>
      <div id="view">
        {route.kind === "reports" && <Reports />}
        {route.kind === "restaurants-pending" && (
          <AllRestaurants defaultStatus="pending" />
        )}
        {route.kind === "restaurants-all" && <AllRestaurants />}
        {route.kind === "listings" && <Listings />}
        {route.kind === "safety-notes" && <SafetyNotes />}
        {route.kind === "content" && (
          <Content kind={route.targetKind} id={route.id} />
        )}
      </div>
    </div>
  );
}
